import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Calculator,
  Plus,
  RefreshCw,
  Loader2,
  Edit,
  Trash2,
  Save,
  X
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import { logger } from '@/lib/logger';

interface CostSummary {
  totalRevenue: number;
  totalCosts: number;
  grossProfit: number;
  grossMargin: number;
  costPerNorth: number;
  mrr: number;
  activeNorths: number;
}

interface ServiceCost {
  serviceName: string;
  totalCalls: number;
  totalCost: number;
  percentage: number;
}

interface MonthlyData {
  month: string;
  revenue: number;
  costs: number;
  profit: number;
}

interface FixedCostEntry {
  service_name: string;
  cost_usd: number;
  notes: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export function CostAnalyticsDashboard() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState<CostSummary>({
    totalRevenue: 0,
    totalCosts: 0,
    grossProfit: 0,
    grossMargin: 0,
    costPerNorth: 0,
    mrr: 0,
    activeNorths: 0,
  });
  const [serviceCosts, setServiceCosts] = useState<ServiceCost[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [fixedCosts, setFixedCosts] = useState<any[]>([]);
  const [newFixedCost, setNewFixedCost] = useState<FixedCostEntry>({
    service_name: '',
    cost_usd: 0,
    notes: '',
  });
  const [addingFixedCost, setAddingFixedCost] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingCost, setEditingCost] = useState<FixedCostEntry>({
    service_name: '',
    cost_usd: 0,
    notes: '',
  });
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    loadAllData();
  }, [selectedMonth]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCostSummary(),
        loadServiceCosts(),
        loadMonthlyTrends(),
        loadFixedCosts(),
      ]);
    } catch (error) {
      logger.error('Error loading cost analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load cost analytics data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCostSummary = async () => {
    // Get current month boundaries
    const [year, month] = selectedMonth.split('-').map(Number);
    const startOfMonth = new Date(year, month - 1, 1).toISOString();
    const endOfMonth = new Date(year, month, 0, 23, 59, 59).toISOString();

    // Get MRR from active subscriptions
    const { data: subscriptions } = await supabase
      .from('team_subscriptions')
      .select('plan_tier')
      .eq('status', 'active');

    let mrr = 0;
    subscriptions?.forEach((sub: any) => {
      switch (sub.plan_tier) {
        case 'lite': mrr += 50; break;
        case 'pro': mrr += 149; break;
        case 'premium': mrr += 249; break;
      }
    });

    // Get actual revenue from revenue_events this month
    const { data: revenueEvents } = await supabase
      .from('revenue_events')
      .select('net_amount_usd')
      .eq('event_type', 'payment_succeeded')
      .gte('created_at', startOfMonth)
      .lte('created_at', endOfMonth);

    const totalRevenue = revenueEvents?.reduce((sum: number, e: any) => sum + Number(e.net_amount_usd), 0) || 0;

    // Get API costs this month
    const { data: apiCosts } = await supabase
      .from('api_call_logs')
      .select('cost_usd')
      .gte('created_at', startOfMonth)
      .lte('created_at', endOfMonth);

    const variableCosts = apiCosts?.reduce((sum: number, log: any) => sum + Number(log.cost_usd || 0), 0) || 0;

    // Get fixed costs this month
    const { data: fixedCostsData } = await supabase
      .from('fixed_costs')
      .select('cost_usd')
      .eq('month', `${year}-${String(month).padStart(2, '0')}-01`);

    const fixedCostsTotal = fixedCostsData?.reduce((sum: number, cost: any) => sum + Number(cost.cost_usd), 0) || 0;

    const totalCosts = variableCosts + fixedCostsTotal;
    const grossProfit = totalRevenue - totalCosts;
    const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

    // Get active norths count
    const { count: activeNorths } = await supabase
      .from('norths')
      .select('*', { count: 'exact', head: true })
      .eq('is_published', true);

    const costPerNorth = activeNorths && activeNorths > 0 ? variableCosts / activeNorths : 0;

    setSummary({
      totalRevenue,
      totalCosts,
      grossProfit,
      grossMargin,
      costPerNorth,
      mrr,
      activeNorths: activeNorths || 0,
    });
  };

  const loadServiceCosts = async () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const startOfMonth = new Date(year, month - 1, 1).toISOString();
    const endOfMonth = new Date(year, month, 0, 23, 59, 59).toISOString();

    const { data } = await supabase
      .from('api_call_logs')
      .select('service_name, cost_usd')
      .gte('created_at', startOfMonth)
      .lte('created_at', endOfMonth);

    if (!data) return;

    // Aggregate by service
    const serviceMap = new Map<string, { calls: number; cost: number }>();

    data.forEach((log: any) => {
      const service = log.service_name || 'openai';
      const current = serviceMap.get(service) || { calls: 0, cost: 0 };
      serviceMap.set(service, {
        calls: current.calls + 1,
        cost: current.cost + Number(log.cost_usd || 0),
      });
    });

    const totalCost = Array.from(serviceMap.values()).reduce((sum, s) => sum + s.cost, 0);

    const costs: ServiceCost[] = Array.from(serviceMap.entries()).map(([name, data]) => ({
      serviceName: name,
      totalCalls: data.calls,
      totalCost: data.cost,
      percentage: totalCost > 0 ? (data.cost / totalCost) * 100 : 0,
    }));

    // Sort by cost descending
    costs.sort((a, b) => b.totalCost - a.totalCost);
    setServiceCosts(costs);
  };

  const loadMonthlyTrends = async () => {
    // Get last 6 months of data
    const months: MonthlyData[] = [];

    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthStr = `${year}-${String(month).padStart(2, '0')}`;
      const monthLabel = date.toLocaleString('default', { month: 'short', year: '2-digit' });

      const startOfMonth = new Date(year, month - 1, 1).toISOString();
      const endOfMonth = new Date(year, month, 0, 23, 59, 59).toISOString();

      // Get revenue
      const { data: revenueData } = await supabase
        .from('revenue_events')
        .select('net_amount_usd')
        .eq('event_type', 'payment_succeeded')
        .gte('created_at', startOfMonth)
        .lte('created_at', endOfMonth);

      const revenue = revenueData?.reduce((sum: number, e: any) => sum + Number(e.net_amount_usd), 0) || 0;

      // Get costs
      const { data: costData } = await supabase
        .from('api_call_logs')
        .select('cost_usd')
        .gte('created_at', startOfMonth)
        .lte('created_at', endOfMonth);

      const { data: fixedData } = await supabase
        .from('fixed_costs')
        .select('cost_usd')
        .eq('month', `${year}-${String(month).padStart(2, '0')}-01`);

      const variableCosts = costData?.reduce((sum: number, log: any) => sum + Number(log.cost_usd || 0), 0) || 0;
      const fixedCosts = fixedData?.reduce((sum: number, cost: any) => sum + Number(cost.cost_usd), 0) || 0;
      const totalCosts = variableCosts + fixedCosts;

      months.push({
        month: monthLabel,
        revenue,
        costs: totalCosts,
        profit: revenue - totalCosts,
      });
    }

    setMonthlyData(months);
  };

  const loadFixedCosts = async () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const monthDate = `${year}-${String(month).padStart(2, '0')}-01`;

    const { data } = await supabase
      .from('fixed_costs')
      .select('*')
      .eq('month', monthDate)
      .order('service_name');

    setFixedCosts(data || []);
  };

  const addFixedCost = async () => {
    if (!newFixedCost.service_name || newFixedCost.cost_usd <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter service name and cost',
        variant: 'destructive',
      });
      return;
    }

    setAddingFixedCost(true);
    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const monthDate = `${year}-${String(month).padStart(2, '0')}-01`;

      const { error } = await supabase
        .from('fixed_costs')
        .upsert({
          month: monthDate,
          service_name: newFixedCost.service_name,
          cost_usd: newFixedCost.cost_usd,
          notes: newFixedCost.notes,
        }, {
          onConflict: 'month,service_name',
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Fixed cost added successfully',
      });

      setNewFixedCost({ service_name: '', cost_usd: 0, notes: '' });
      await loadAllData();
    } catch (error) {
      logger.error('Error adding fixed cost:', error);
      toast({
        title: 'Error',
        description: 'Failed to add fixed cost',
        variant: 'destructive',
      });
    } finally {
      setAddingFixedCost(false);
    }
  };

  const startEdit = (cost: any) => {
    setEditingId(cost.id);
    setEditingCost({
      service_name: cost.service_name,
      cost_usd: cost.cost_usd,
      notes: cost.notes || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingCost({ service_name: '', cost_usd: 0, notes: '' });
  };

  const saveEdit = async () => {
    if (!editingCost.service_name || editingCost.cost_usd <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter service name and cost',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('fixed_costs')
        .update({
          service_name: editingCost.service_name,
          cost_usd: editingCost.cost_usd,
          notes: editingCost.notes,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Fixed cost updated successfully',
      });

      setEditingId(null);
      setEditingCost({ service_name: '', cost_usd: 0, notes: '' });
      await loadAllData();
    } catch (error) {
      logger.error('Error updating fixed cost:', error);
      toast({
        title: 'Error',
        description: 'Failed to update fixed cost',
        variant: 'destructive',
      });
    }
  };

  const deleteFixedCost = async (id: string) => {
    if (!confirm('Are you sure you want to delete this fixed cost?')) {
      return;
    }

    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('fixed_costs')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Fixed cost deleted successfully',
      });

      await loadAllData();
    } catch (error) {
      logger.error('Error deleting fixed cost:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete fixed cost',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Month Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Cost Analytics
          </h2>
          <p className="text-muted-foreground">Track expenses, revenue, and profitability</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>Month:</Label>
            <Input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-40"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRefreshing(true);
              loadAllData().finally(() => setRefreshing(false));
            }}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(summary.totalRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">
              MRR: {formatCurrency(summary.mrr)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Costs</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(summary.totalCosts)}
            </div>
            <p className="text-xs text-muted-foreground">
              Fixed + Variable costs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gross Profit</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.grossProfit)}
            </div>
            <p className="text-xs text-muted-foreground">
              Margin: {summary.grossMargin.toFixed(1)}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost Per North</CardTitle>
            <Calculator className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.costPerNorth)}
            </div>
            <p className="text-xs text-muted-foreground">
              {summary.activeNorths} active reports
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="breakdown" className="space-y-4">
        <TabsList>
          <TabsTrigger value="breakdown">Cost Breakdown</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="fixed">Fixed Costs</TabsTrigger>
        </TabsList>

        <TabsContent value="breakdown" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Service Cost Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">API Cost Breakdown</CardTitle>
                <CardDescription>Variable costs by service this month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {serviceCosts.length > 0 ? (
                    serviceCosts.map((service) => (
                      <div key={service.serviceName} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="font-medium capitalize">{service.serviceName}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatNumber(service.totalCalls)} calls
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">{formatCurrency(service.totalCost)}</div>
                          <Badge variant="secondary">{service.percentage.toFixed(1)}%</Badge>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-4">
                      No API calls recorded this month
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cost Distribution</CardTitle>
                <CardDescription>Visual breakdown of API costs</CardDescription>
              </CardHeader>
              <CardContent>
                {serviceCosts.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPieChart>
                      <Pie
                        data={serviceCosts}
                        dataKey="totalCost"
                        nameKey="serviceName"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ serviceName, percentage }) =>
                          `${serviceName}: ${percentage.toFixed(1)}%`
                        }
                      >
                        {serviceCosts.map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                    No data to display
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Profitability Trend (Last 6 Months)</CardTitle>
              <CardDescription>Revenue, costs, and profit over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Revenue"
                  />
                  <Line
                    type="monotone"
                    dataKey="costs"
                    stroke="#ef4444"
                    strokeWidth={2}
                    name="Costs"
                  />
                  <Line
                    type="monotone"
                    dataKey="profit"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    name="Profit"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Monthly Comparison</CardTitle>
              <CardDescription>Bar chart view of financials</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${value}`} />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#10b981" name="Revenue" />
                  <Bar dataKey="costs" fill="#ef4444" name="Costs" />
                  <Bar dataKey="profit" fill="#3b82f6" name="Profit" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fixed" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Fixed Monthly Costs</CardTitle>
              <CardDescription>
                Track infrastructure and service fees for {new Date(selectedMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Fixed Cost Form */}
              <div className="grid gap-4 p-4 border rounded-lg bg-muted/50">
                <h4 className="font-medium flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add Fixed Cost
                </h4>
                <div className="grid gap-4 md:grid-cols-4">
                  <div>
                    <Label>Service Name</Label>
                    <Select
                      value={newFixedCost.service_name}
                      onValueChange={(value) => setNewFixedCost({ ...newFixedCost, service_name: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="supabase_pro">Supabase Pro Plan</SelectItem>
                        <SelectItem value="supabase_compute">Supabase Compute</SelectItem>
                        <SelectItem value="vercel">Vercel</SelectItem>
                        <SelectItem value="zillow_base">Zillow API Base</SelectItem>
                        <SelectItem value="resend">Resend</SelectItem>
                        <SelectItem value="publer">Publer</SelectItem>
                        <SelectItem value="instagram_api">Instagram API</SelectItem>
                        <SelectItem value="facebook_api">Facebook API</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cost (USD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newFixedCost.cost_usd || ''}
                      onChange={(e) => setNewFixedCost({ ...newFixedCost, cost_usd: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Input
                      value={newFixedCost.notes}
                      onChange={(e) => setNewFixedCost({ ...newFixedCost, notes: e.target.value })}
                      placeholder="Optional notes"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={addFixedCost} disabled={addingFixedCost} className="w-full">
                      {addingFixedCost ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Add Cost'
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Fixed Costs List */}
              <div className="space-y-2">
                {fixedCosts.length > 0 ? (
                  fixedCosts.map((cost: any) => (
                    <div key={cost.id} className="p-3 border rounded-lg">
                      {editingId === cost.id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <div className="grid gap-3 md:grid-cols-4">
                            <div>
                              <Label>Service Name</Label>
                              <Select
                                value={editingCost.service_name}
                                onValueChange={(value) => setEditingCost({ ...editingCost, service_name: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select service" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="supabase_pro">Supabase Pro Plan</SelectItem>
                                  <SelectItem value="supabase_compute">Supabase Compute</SelectItem>
                                  <SelectItem value="vercel">Vercel</SelectItem>
                                  <SelectItem value="zillow_base">Zillow API Base</SelectItem>
                                  <SelectItem value="resend">Resend</SelectItem>
                                  <SelectItem value="publer">Publer</SelectItem>
                                  <SelectItem value="instagram_api">Instagram API</SelectItem>
                                  <SelectItem value="facebook_api">Facebook API</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Cost (USD)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editingCost.cost_usd || ''}
                                onChange={(e) => setEditingCost({ ...editingCost, cost_usd: parseFloat(e.target.value) || 0 })}
                                placeholder="0.00"
                              />
                            </div>
                            <div>
                              <Label>Notes</Label>
                              <Input
                                value={editingCost.notes}
                                onChange={(e) => setEditingCost({ ...editingCost, notes: e.target.value })}
                                placeholder="Optional notes"
                              />
                            </div>
                            <div className="flex items-end gap-2">
                              <Button onClick={saveEdit} size="sm" className="flex-1">
                                <Save className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                              <Button onClick={cancelEdit} variant="outline" size="sm">
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium capitalize">
                              {cost.service_name.replace(/_/g, ' ')}
                            </div>
                            {cost.notes && (
                              <div className="text-sm text-muted-foreground">{cost.notes}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="font-bold">{formatCurrency(Number(cost.cost_usd))}</div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEdit(cost)}
                                disabled={editingId !== null}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteFixedCost(cost.id)}
                                disabled={deletingId === cost.id || editingId !== null}
                              >
                                {deletingId === cost.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    No fixed costs recorded for this month. Add your infrastructure costs above.
                  </p>
                )}
              </div>

              {fixedCosts.length > 0 && (
                <div className="pt-4 border-t">
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total Fixed Costs:</span>
                    <span>
                      {formatCurrency(fixedCosts.reduce((sum: number, cost: any) => sum + Number(cost.cost_usd), 0))}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
